import { NextRequest } from 'next/server';
import axios, { AxiosError } from 'axios';
import { logger } from './logger';

/**
 * Extract client IP address from NextRequest
 * Handles various proxy headers (X-Forwarded-For, X-Real-IP, etc.)
 *
 * @param request - NextRequest object
 * @returns IP address string or null
 */
export function getClientIP(request: NextRequest): string | null {
  // Check various headers for IP address (handles proxies/load balancers)
  const forwardedFor = request.headers.get('x-forwarded-for');
  if (forwardedFor) {
    // X-Forwarded-For can contain multiple IPs, take the first one
    return forwardedFor.split(',')[0].trim();
  }

  const realIP = request.headers.get('x-real-ip');
  if (realIP) {
    return realIP.trim();
  }

  const cfConnectingIP = request.headers.get('cf-connecting-ip'); // Cloudflare
  if (cfConnectingIP) {
    return cfConnectingIP.trim();
  }

  // Fallback to remote address if available
  return null;
}

/**
 * Parse user agent string to extract device information
 *
 * @param userAgent - User agent string
 * @returns Device information string
 */
export function parseDeviceFromUserAgent(userAgent: string | null): string {
  if (!userAgent) {
    return 'Unknown Device';
  }

  const ua = userAgent.toLowerCase();

  // Detect mobile devices
  if (
    /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)
  ) {
    if (/iphone/i.test(ua)) return 'iPhone';
    if (/ipad/i.test(ua)) return 'iPad';
    if (/android/i.test(ua)) {
      // Try to extract Android version
      const androidMatch = ua.match(/android\s([0-9\.]*)/);
      return androidMatch ? `Android ${androidMatch[1]}` : 'Android Device';
    }
    if (/ipod/i.test(ua)) return 'iPod';
    if (/blackberry/i.test(ua)) return 'BlackBerry';
    return 'Mobile Device';
  }

  // Detect desktop browsers
  if (/windows/i.test(ua)) {
    if (/edg/i.test(ua)) return 'Windows - Edge';
    if (/chrome/i.test(ua)) return 'Windows - Chrome';
    if (/firefox/i.test(ua)) return 'Windows - Firefox';
    if (/safari/i.test(ua)) return 'Windows - Safari';
    return 'Windows';
  }

  if (/macintosh|mac os x/i.test(ua)) {
    if (/edg/i.test(ua)) return 'macOS - Edge';
    if (/chrome/i.test(ua)) return 'macOS - Chrome';
    if (/firefox/i.test(ua)) return 'macOS - Firefox';
    if (/safari/i.test(ua)) return 'macOS - Safari';
    return 'macOS';
  }

  if (/linux/i.test(ua)) {
    if (/chrome/i.test(ua)) return 'Linux - Chrome';
    if (/firefox/i.test(ua)) return 'Linux - Firefox';
    return 'Linux';
  }

  // Fallback
  return 'Unknown Device';
}

/**
 * Get location from IP address using ip-api.com (free tier)
 * Note: Free tier allows 45 requests/minute. For higher limits, consider:
 * - ipapi.co (paid plans)
 * - MaxMind GeoIP2 (self-hosted)
 *
 * @param ip - IP address
 * @returns Location string (city, country) or null
 */
export async function getLocationFromIP(
  ip: string | null,
): Promise<string | null> {
  if (!ip || ip === '::1' || ip === '127.0.0.1') {
    return 'Local';
  }

  // Skip private/local IP ranges
  if (
    ip.startsWith('192.168.') ||
    ip.startsWith('10.') ||
    ip.startsWith('172.16.') ||
    ip.startsWith('172.17.') ||
    ip.startsWith('172.18.') ||
    ip.startsWith('172.19.') ||
    ip.startsWith('172.20.') ||
    ip.startsWith('172.21.') ||
    ip.startsWith('172.22.') ||
    ip.startsWith('172.23.') ||
    ip.startsWith('172.24.') ||
    ip.startsWith('172.25.') ||
    ip.startsWith('172.26.') ||
    ip.startsWith('172.27.') ||
    ip.startsWith('172.28.') ||
    ip.startsWith('172.29.') ||
    ip.startsWith('172.30.') ||
    ip.startsWith('172.31.')
  ) {
    return 'Local Network';
  }

  try {
    // Use ip-api.com free tier (no API key required)
    // Free tier: 45 requests/minute, no HTTPS
    const response = await axios.get<{
      status: string;
      message?: string;
      country?: string;
      regionName?: string;
      city?: string;
    }>(`http://ip-api.com/json/${ip}`, {
      params: {
        fields: 'status,message,country,regionName,city',
      },
      timeout: 5000, // 5 second timeout
    });

    const data = response.data;

    if (data.status === 'success') {
      const locationParts = [data.city, data.regionName, data.country]
        .filter(Boolean)
        .map((part) => part?.trim())
        .filter(Boolean);

      if (locationParts.length > 0) {
        // Format: "City, Region, Country" or "City, Country" or just "Country"
        return locationParts.join(', ');
      }
    } else {
      logger.warn('IP geolocation API returned error', {
        ip,
        message: data.message,
      });
    }
  } catch (error) {
    // Handle axios errors (timeout, network errors, etc.)
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (
        axiosError.code === 'ECONNABORTED' ||
        axiosError.message.includes('timeout')
      ) {
        logger.warn('IP geolocation request timed out', { ip });
      } else if (axiosError.response) {
        logger.warn('IP geolocation API returned error', {
          ip,
          status: axiosError.response.status,
          statusText: axiosError.response.statusText,
        });
      } else {
        logger.warn('IP geolocation request failed', {
          ip,
          message: axiosError.message,
        });
      }
    } else {
      logger.warn('Failed to get location from IP', { error, ip });
    }
  }

  return null;
}

/**
 * Extract session metadata from NextRequest
 *
 * @param request - NextRequest object
 * @returns Session metadata object
 */
export async function extractSessionMetadata(request: NextRequest): Promise<{
  userAgent: string | null;
  ipAddress: string | null;
  device: string;
  location: string | null;
}> {
  const userAgent = request.headers.get('user-agent');
  const ipAddress = getClientIP(request);
  const device = parseDeviceFromUserAgent(userAgent);
  const location = await getLocationFromIP(ipAddress);

  return {
    userAgent,
    ipAddress,
    device,
    location: location || 'Unknown',
  };
}
